import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { initFlowbite } from 'flowbite';
import { finalize } from 'rxjs/operators';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { SITE_SECURITY_FORM } from '../../../shared/definitions/site-security.definition';
import {
  SITE_SECURITY_INTRO_INSTRUCTIONS,
  SITE_SECURITY_QUESTION_META
} from '../../../shared/definitions/site-security.meta';
import {
  LikertValue,
  SecurityInfoSection,
  SecuritySectionDefinition,
  SecurityQuestionMeta
} from '../../../shared/models/form-definition';
import { EFormType } from '../../../shared/models/types.enum';
import { FormsService } from '../../../shared/services/forms.service';
import { UserDataService } from '../../../shared/services/userData.service';

type QuestionGroup = FormGroup<{
  value: FormControl<LikertValue | null>;
  consideration: FormControl<string>;
}>;

type IntroConfirmationMap = Record<string, FormControl<boolean>>;

@Component({
  selector: 'app-site-security-form',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    SpinnerComponent, 
    PopupMessageComponent
  ],
  templateUrl: './site-security-form.component.html'
})
export class AlSiteSecurityFormComponent {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly formsService = inject(FormsService);
  private readonly userDataService = inject(UserDataService);
  @ViewChild('scrollContainer') private readonly scrollContainer?: ElementRef<HTMLDivElement>;
  readonly definition = SITE_SECURITY_FORM;
  readonly introInstructions = SITE_SECURITY_INTRO_INSTRUCTIONS;
  readonly questionMeta = SITE_SECURITY_QUESTION_META;

  readonly questionGroups: Record<string, QuestionGroup> = this.buildQuestionGroups();
  readonly introConfirmations: IntroConfirmationMap = this.buildIntroConfirmations();
  currentSectionIndex = 0;
  readonly loading = signal(false);
  readonly messageModal = signal<string | null>(null);
  readonly popupMessage = signal<string | null>(null);

  constructor() {
    this.attachConsiderationAutoFill();
    this.refreshTooltips();
  }

  trackByKey(_index: number, item: { key: string }): string {
    return item.key;
  }

  getQuestionGroup(key: string): QuestionGroup {
    return this.questionGroups[key];
  }

  getMetaForQuestion(key: string): SecurityQuestionMeta | undefined {
    const directMatch = this.questionMeta[key];
    if (directMatch) {
      return directMatch;
    }

    const capitalizedMatch = this.questionMeta[key.charAt(0).toUpperCase() + key.slice(1)];
    return capitalizedMatch;
  }

  getMoreInfoSections(key: string): SecurityInfoSection[] {
    return this.getMetaForQuestion(key)?.moreInfo ?? [];
  }

  getIntroConfirmationControl(section: SecuritySectionDefinition): FormControl<boolean> {
    if (!section.introKey) {
      throw new Error('Intro confirmation requested for a section without an introKey.');
    }

    const control = this.introConfirmations[section.introKey];
    if (!control) {
      throw new Error(`Missing intro confirmation control for section ${section.key}.`);
    }

    return control;
  }

  get currentSection(): SecuritySectionDefinition {
    return this.definition.sections[this.currentSectionIndex];
  }

  get isFirstSection(): boolean {
    return this.currentSectionIndex === 0;
  }

  get isLastSection(): boolean {
    return this.currentSectionIndex === this.definition.sections.length - 1;
  }

  get currentSectionProgressLabel(): string {
    return `${this.currentSectionIndex + 1} / ${this.definition.sections.length}`;
  }

  formatRatingLabel(value: string): string {
    return value.replace(/_/g, ' ');
  }

  getRatingLabelClass(value: string): string {
    switch (value) {
      case 'Very_Low':
        return 'text-red-700';
      case 'Low':
        return 'text-orange-600';
      case 'Medium':
        return 'text-yellow-600';
      case 'High':
        return 'text-green-600';
      case 'Very_High':
        return 'text-green-800';
      default:
        return 'text-slate-500';
    }
  }

  getSuggestedConsideration(key: string): string {
    const group = this.getQuestionGroup(key);
    const value = group.controls.value.value;
    if (!value) {
      return '';
    }
    return this.getMetaForQuestion(key)?.thingsToConsider?.[value] ?? '';
  }

  canProceedFromSection(section: SecuritySectionDefinition): boolean {
    const introConfirmed = !section.introKey || this.introConfirmations[section.introKey]?.value === true;
    if (!introConfirmed) {
      return false;
    }

    return section.questions.every((question) => {
      const value = this.questionGroups[question.key].controls.value.value;
      return typeof value === 'string' && value.length > 0;
    });
  }

  canMoveToSection(sectionIndex: number): boolean {
    if (sectionIndex <= this.currentSectionIndex) {
      return true;
    }

    return this.definition.sections
      .slice(0, sectionIndex)
      .every((section) => this.canProceedFromSection(section));
  }

  goToSection(sectionIndex: number): void {
    if (!this.canMoveToSection(sectionIndex)) {
      return;
    }

    this.currentSectionIndex = sectionIndex;
    this.scrollToTop();
    this.refreshTooltips();
  }

  previousSection(): void {
    if (!this.isFirstSection) {
      this.currentSectionIndex -= 1;
      this.scrollToTop();
      this.refreshTooltips();
    }
  }

  nextSection(): void {
    if (!this.canProceedFromSection(this.currentSection) || this.isLastSection) {
      return;
    }

    this.currentSectionIndex += 1;
    this.scrollToTop();
    this.refreshTooltips();
  }

  onBack(): void {
    const buildingId = this.userDataService.getBuildingId();
    if (!buildingId) {
      return;
    }

    this.router.navigate(['/building', buildingId, 'form-dashboard']);
  }

  submit(): void {
    const data: Record<string, unknown> = {};

    for (const section of this.definition.sections) {
      if (section.introKey) {
        data[section.introKey] = true;
      }

      for (const question of section.questions) {
        const group = this.questionGroups[question.key];
        const value = group.controls.value.value;
        const consideration = group.controls.consideration.value;
        const selectedOption = this.getMetaForQuestion(question.key)?.options.find(
          (option) => option.value === value
        );
        data[question.key] = {
          label: selectedOption?.label ?? '',
          value: value ?? '',
          consideration: consideration ?? ''
        };
      }
    }

    const buildingId = this.userDataService.getBuildingId();
    if (!buildingId) {
      this.messageModal.set('unsuccess');
      this.popupMessage.set('Missing building ID.');
      return;
    }

    console.log('[site-security] submit start', { buildingId, data });
    this.loading.set(true);
    this.formsService.submitForm({
      urlParams: {
        buildingId,
        formType: EFormType.SiteSecurity
      },
      body: data
    }).pipe(
      finalize(() => {
        this.loading.set(false);
      })
    ).subscribe({
      next: (response) => {
        this.messageModal.set('success');
        this.popupMessage.set('Submit successful!');
        this.router.navigate(['/building', buildingId, 'form-dashboard']);
      },
      error: (error) => {
        this.messageModal.set('unsuccess');
        this.popupMessage.set(error.message);
        console.error('Error submitting site security form:', error.message);
      }
    });
  }

  resetMessageModal(): void {
    this.messageModal.set(null);
    this.popupMessage.set(null);
  }

  private buildQuestionGroups(): Record<string, QuestionGroup> {
    const groups: Record<string, QuestionGroup> = {};

    for (const section of this.definition.sections) {
      for (const question of section.questions) {
        groups[question.key] = new FormGroup({
          value: new FormControl<LikertValue | null>(null, { validators: [Validators.required] }),
          consideration: new FormControl('', { nonNullable: true })
        });
      }
    }

    return groups;
  }

  private buildIntroConfirmations(): IntroConfirmationMap {
    const controls: IntroConfirmationMap = {};

    for (const section of this.definition.sections) {
      if (section.introKey) {
        controls[section.introKey] = new FormControl(false, { nonNullable: true, validators: [Validators.requiredTrue] });
      }
    }

    return controls;
  }

  private attachConsiderationAutoFill(): void {
    for (const section of this.definition.sections) {
      for (const question of section.questions) {
        const group = this.questionGroups[question.key];
        group.controls.value.valueChanges.subscribe((selectedValue) => {
          if (!selectedValue) {
            return;
          }
          const recommendation = this.getMetaForQuestion(question.key)?.thingsToConsider?.[selectedValue];
          if (recommendation) {
            group.controls.consideration.setValue(recommendation, { emitEvent: false });
          }
        });
      }
    }
  }

  private scrollToTop(): void {
    this.scrollContainer?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    this.document.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private refreshTooltips(): void {
    this.document.defaultView?.setTimeout(() => {
      initFlowbite();
    }, 0);
  }
}
